// Tremor Raw Checkbox [v0.0.2]

import type { ReactNode } from 'react'
import { Checkbox as CheckboxPrimitives } from 'radix-ui'
import { cn, focusRingClassName, tv } from '@joyful/utils'
import type { SetRequired } from 'type-fest'
import type { LabelProps } from './Label'
import { Label } from './Label'

function CheckboxIndicator({
  variant,
  ...props
}: React.ComponentProps<'svg'> & {
  variant: 'indeterminate' | 'checked'
}) {
  return (
    <svg
      aria-hidden="true"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      {variant === 'indeterminate' ? (
        <line
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="2"
          x1="4"
          x2="12"
          y1="8"
          y2="8"
        />
      ) : (
        <path
          d="M11.2 5.59998L6.79999 9.99998L4.79999 7.99998"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      )}
    </svg>
  )
}

const checkboxVariants = tv({
  base: [
    // base
    'relative inline-flex size-4 shrink-0 appearance-none items-center justify-center rounded-sm shadow-xs ring-1 outline-hidden transition duration-100 ring-inset enabled:cursor-pointer',
    // text color
    'text-tremor-content-inverted',
    // background color
    'bg-tremor-background',
    // ring color
    'ring-gray-300 dark:ring-tremor-border',
    // disabled
    'data-disabled:bg-tremor-background-subtle data-disabled:text-tremor-content-subtle data-disabled:ring-gray-300 dark:data-disabled:ring-tremor-border',
    // checked and enabled
    'enabled:data-[state=checked]:bg-tremor-brand enabled:data-[state=checked]:ring-0 enabled:data-[state=checked]:ring-transparent',
    // indeterminate
    'enabled:data-[state=indeterminate]:bg-tremor-brand enabled:data-[state=indeterminate]:ring-0 enabled:data-[state=indeterminate]:ring-transparent',
    // focus
    focusRingClassName,
  ],

  slots: {
    indicator: 'flex size-full items-center justify-center',
  },
})

export type CheckboxProps = React.ComponentProps<typeof CheckboxPrimitives.Root>

export function Checkbox({ checked, className, ...props }: CheckboxProps) {
  const classNames = checkboxVariants()

  return (
    <CheckboxPrimitives.Root
      {...props}
      checked={checked}
      className={cn(classNames.base(), className)}
    >
      <CheckboxPrimitives.Indicator asChild className={classNames.indicator()}>
        <CheckboxIndicator
          variant={checked === 'indeterminate' ? 'indeterminate' : 'checked'}
        />
      </CheckboxPrimitives.Indicator>
    </CheckboxPrimitives.Root>
  )
}

export function CheckboxRawIndicator({
  className,
  isSelected,
  ...restProps
}: React.ComponentProps<'div'> & { isSelected: boolean }) {
  const checkboxClassNames = checkboxVariants()

  return (
    <div
      className={cn(checkboxClassNames.base(), 'text-tremor-brand', className)}
      {...restProps}
    >
      {isSelected === true ? (
        <CheckboxIndicator
          className={checkboxClassNames.indicator()}
          variant="checked"
        />
      ) : null}
    </div>
  )
}

export interface LabeledCheckboxProps extends SetRequired<CheckboxProps, 'id'> {
  children: ReactNode
  labelProps?: Omit<LabelProps, 'children' | 'htmlFor'>
}

export function LabeledCheckbox({
  children,
  className,
  id,
  labelProps,
  ...props
}: LabeledCheckboxProps) {
  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <Checkbox id={id} {...props} />
      <Label size="sm" htmlFor={id} {...labelProps}>
        {children}
      </Label>
    </div>
  )
}
